import type {
  DashboardType,
  UserDashboardPreferences,
  UpdateDashboardPreferences,
  UpdateWidgetPreference,
  SuggestedActionsResponse,
  SuggestedActionsCount,
  TimeFilter,
  WidgetConfig,
  UserWidgetPreference,
} from '@/types/dashboard';
import { getModuleMode } from '@/lib/feature-mode';
import { getAuthConfig } from '@/lib/auth-fetch';

// Helper function to get current data source mode from feature settings
const getDataSource = (): 'demo' | 'api' => {
  const mode = getModuleMode('dashboard');
  if (mode === 'demo') return 'demo';
  return 'api';
};

// Default widget configurations for Financial Dashboard
const financialWidgets: UserWidgetPreference[] = [
  { id: '1', userId: 'demo-user', dashboardType: 'financial', widgetId: 'quick-actions-financial', isEnabled: true, displayOrder: 1 },
  { id: '2', userId: 'demo-user', dashboardType: 'financial', widgetId: 'kpi-total-asset-value', isEnabled: true, displayOrder: 2 },
  { id: '3', userId: 'demo-user', dashboardType: 'financial', widgetId: 'kpi-acquisition-cost', isEnabled: true, displayOrder: 3 },
  { id: '4', userId: 'demo-user', dashboardType: 'financial', widgetId: 'kpi-total-depreciation', isEnabled: true, displayOrder: 4 },
  { id: '5', userId: 'demo-user', dashboardType: 'financial', widgetId: 'kpi-average-asset-value', isEnabled: true, displayOrder: 5 },
  { id: '7', userId: 'demo-user', dashboardType: 'financial', widgetId: 'kpi-current-period', isEnabled: true, displayOrder: 6 },
  { id: '9', userId: 'demo-user', dashboardType: 'financial', widgetId: 'depreciation-by-category', isEnabled: true, displayOrder: 7 },

  { id: '11', userId: 'demo-user', dashboardType: 'financial', widgetId: 'top-assets-by-value', isEnabled: true, displayOrder: 11 },
  { id: '12', userId: 'demo-user', dashboardType: 'financial', widgetId: 'assets-by-category', isEnabled: true, displayOrder: 12 },
  { id: '13', userId: 'demo-user', dashboardType: 'financial', widgetId: 'assets-by-location', isEnabled: true, displayOrder: 13 },
  { id: '14', userId: 'demo-user', dashboardType: 'financial', widgetId: 'assets-by-status', isEnabled: true, displayOrder: 14 },
];

// Default widget configurations for Asset Management Dashboard
const assetManagementWidgets: UserWidgetPreference[] = [
  { id: '101', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'quick-actions-asset-management', isEnabled: true, displayOrder: 1 },
  { id: '102', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'kpi-total-assets', isEnabled: true, displayOrder: 2 },
  { id: '103', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'kpi-active-assets', isEnabled: true, displayOrder: 3 },
  { id: '104', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'kpi-maintenance-due', isEnabled: true, displayOrder: 4 },
  { id: '105', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'kpi-audits-pending', isEnabled: true, displayOrder: 5 },
  { id: '107', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'kpi-asset-health', isEnabled: true, displayOrder: 6 },
  { id: '108', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'asset-mgmt-health-overview', isEnabled: true, displayOrder: 7 },
  { id: '109', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'asset-mgmt-maintenance-insurance-due', isEnabled: true, displayOrder: 8 },
  { id: '110', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'asset-mgmt-audit-status', isEnabled: true, displayOrder: 9 },
  { id: '111', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'asset-mgmt-exception-alerts', isEnabled: true, displayOrder: 10 },
  // Compliance widgets (Phase 5) - Moved before lifecycle stage
  { id: '118', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'compliance-summary', isEnabled: true, displayOrder: 11 },
  { id: '119', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'upcoming-inspections', isEnabled: true, displayOrder: 12 },
  { id: '120', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'overdue-inspections', isEnabled: true, displayOrder: 13 },
  // Asset Lifecycle Stage widget removed as requested
  // { id: '112', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'asset-mgmt-lifecycle-stage', isEnabled: true, displayOrder: 14 },
  { id: '113', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'asset-mgmt-age-analysis', isEnabled: true, displayOrder: 14 },
  { id: '114', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'warranty-status', isEnabled: true, displayOrder: 16 },
  { id: '115', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'assets-by-category', isEnabled: true, displayOrder: 17 },
  { id: '116', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'assets-by-location', isEnabled: true, displayOrder: 18 },
  { id: '117', userId: 'demo-user', dashboardType: 'asset-management', widgetId: 'assets-by-status', isEnabled: true, displayOrder: 19 },
];

// Mock user preferences for demo mode
const getMockUserPreferences = (dashboardType: DashboardType): UserDashboardPreferences => ({
  userId: 'demo-user',
  selectedDashboardType: dashboardType,
  lastAccessedAt: new Date().toISOString(),
  widgets: dashboardType === 'financial' ? financialWidgets : assetManagementWidgets,
});

// Mock suggested actions for demo mode
const mockSuggestedActions: SuggestedActionsResponse = {
  actions: [
    { id: '1', type: 'insurance', title: 'Insurance Renewal Due', description: 'Company Vehicle - Ford Transit insurance expires in 7 days', dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), priority: 'high', status: 'upcoming', linkedEntity: { type: 'asset', id: '3', name: 'Company Vehicle - Ford Transit' } },
    { id: '2', type: 'maintenance', title: 'Scheduled Maintenance', description: 'Office Printer HP LaserJet requires scheduled maintenance', dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), priority: 'medium', status: 'upcoming', linkedEntity: { type: 'asset', id: '2', name: 'Office Printer HP LaserJet' } },
    { id: '3', type: 'audit', title: 'IT Equipment Audit', description: 'Q1 2026 IT Equipment Audit is 71% complete', dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), priority: 'medium', status: 'upcoming', linkedEntity: { type: 'audit', id: '1', name: 'Q1 2026 IT Equipment Audit' } },
    { id: '4', type: 'task', title: 'Review Asset Documentation', description: 'Review and update asset documentation for compliance', dueDate: new Date().toISOString(), priority: 'medium', status: 'today', linkedEntity: { type: 'task', id: '1', name: 'Documentation Review' } },
    { id: '5', type: 'insurance', title: 'Expired Insurance', description: 'MacBook Pro 16" insurance has expired', dueDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), priority: 'high', status: 'overdue', linkedEntity: { type: 'asset', id: '5', name: 'MacBook Pro 16"' } },
  ],
  totalCount: 5,
  page: 1,
  pageSize: 10,
  hasMore: false,
};

const mockSuggestedActionsCount: SuggestedActionsCount = {
  overdue: 1,
  today: 1,
  upcoming: 3,
  total: 5,
};

export class DashboardService {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async buildHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const authConfig = getAuthConfig();
    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    headers['X-Instance-Id'] = authConfig.instanceId ?? 'default-instance';
    return headers;
  }

  // Dashboard Preferences
  async getUserPreferences(): Promise<UserDashboardPreferences> {
    if (getDataSource() === 'demo') {
      return getMockUserPreferences('financial');
    }
    const headers = await this.buildHeaders();
    const response = await fetch(`${this.baseUrl}/api/dashboard/preferences`, {
      headers,
    });
    if (!response.ok) throw new Error('Failed to fetch user preferences');
    return response.json();
  }

  async getDashboardPreferences(
    dashboardType: DashboardType
  ): Promise<UserDashboardPreferences> {
    // Always use hardcoded widget configuration - widgets are the same for all instances
    // The data for each widget is fetched separately by the widget components based on mode
    return getMockUserPreferences(dashboardType);
  }

  async savePreferences(
    preferences: UpdateDashboardPreferences
  ): Promise<UserDashboardPreferences> {
    if (getDataSource() === 'demo') {
      return getMockUserPreferences(preferences.selectedDashboardType);
    }
    const headers = await this.buildHeaders();
    const response = await fetch(`${this.baseUrl}/api/dashboard/preferences`, {
      method: 'POST',
      headers,
      body: JSON.stringify(preferences),
    });
    if (!response.ok) throw new Error('Failed to save preferences');
    return response.json();
  }

  async updateWidgetPreferences(
    dashboardType: DashboardType,
    widgets: UpdateWidgetPreference[]
  ): Promise<UserDashboardPreferences> {
    if (getDataSource() === 'demo') {
      return getMockUserPreferences(dashboardType);
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.baseUrl}/api/dashboard/preferences/${dashboardType}/widgets`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(widgets),
      }
    );
    if (!response.ok) throw new Error('Failed to update widget preferences');
    return response.json();
  }

  async resetToDefaults(dashboardType: DashboardType): Promise<void> {
    if (getDataSource() === 'demo') {
      return;
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.baseUrl}/api/dashboard/preferences/${dashboardType}`,
      {
        method: 'DELETE',
        headers,
      }
    );
    if (!response.ok) throw new Error('Failed to reset preferences');
  }

  async getDefaultWidgets(dashboardType: DashboardType): Promise<WidgetConfig[]> {
    if (getDataSource() === 'demo') {
      const widgets = dashboardType === 'financial' ? financialWidgets : assetManagementWidgets;
      return widgets.map(w => ({
        id: w.widgetId,
        name: w.widgetId.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        description: `${w.widgetId} widget`,
        category: dashboardType,
        defaultEnabled: w.isEnabled,
        defaultOrder: w.displayOrder,
        minWidth: 1,
        minHeight: 1,
      }));
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.baseUrl}/api/dashboard/preferences/${dashboardType}/defaults`,
      {
        headers,
      }
    );
    if (!response.ok) throw new Error('Failed to fetch default widgets');
    return response.json();
  }

  // Suggested Actions
  async getSuggestedActions(
    timeFilter: TimeFilter = 'all',
    page: number = 1,
    pageSize: number = 10
  ): Promise<SuggestedActionsResponse> {
    if (getDataSource() === 'demo') {
      let filteredActions = [...mockSuggestedActions.actions];
      
      // Filter by time range
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const nextWeek = new Date(now);
      nextWeek.setDate(nextWeek.getDate() + 7);
      const nextMonth = new Date(now);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      
      if (timeFilter === 'tomorrow') {
        filteredActions = filteredActions.filter(a => new Date(a.dueDate) <= tomorrow);
      } else if (timeFilter === 'next-week') {
        filteredActions = filteredActions.filter(a => new Date(a.dueDate) <= nextWeek);
      } else if (timeFilter === 'next-month') {
        filteredActions = filteredActions.filter(a => new Date(a.dueDate) <= nextMonth);
      }
      
      const start = (page - 1) * pageSize;
      const paginatedActions = filteredActions.slice(start, start + pageSize);
      
      return {
        actions: paginatedActions,
        totalCount: filteredActions.length,
        page,
        pageSize,
        hasMore: start + pageSize < filteredActions.length,
      };
    }
    const params = new URLSearchParams({
      timeRange: timeFilter,
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.baseUrl}/api/dashboard/suggested-actions?${params}`,
      {
        headers,
      }
    );
    if (!response.ok) throw new Error('Failed to fetch suggested actions');
    return response.json();
  }

  async getSuggestedActionsCount(
    timeFilter: TimeFilter = 'all'
  ): Promise<SuggestedActionsCount> {
    if (getDataSource() === 'demo') {
      return mockSuggestedActionsCount;
    }
    const params = new URLSearchParams({ timeRange: timeFilter });

    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.baseUrl}/api/dashboard/suggested-actions/count?${params}`,
      {
        headers,
      }
    );
    if (!response.ok) throw new Error('Failed to fetch action counts');
    return response.json();
  }
}
